import React, { PropsWithChildren } from 'react';

import { Menu, MenuItem, MenuItems } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import { Caption3, Wrap } from 'components/Common';
import Spacing from 'components/Common/Spacing';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from 'components/ui/sheet';
import { auth } from 'features/signup';
import useIsMobile from 'hooks/useIsMobile';
import useMatchMedia from 'hooks/useMatchMedia';
import { ChevronRight, X as CloseIcon, Menu as MenuIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { userSelectorFamily } from 'recoil/login';
import { ADMIN } from 'shared/constants';
import { generateStorage } from 'shared/lib';
import { LoginStorage } from 'shared/lib';

import {
  Button,
  DropdownBlock,
  DropdownContainer,
  Header,
  LinkButton,
  LogOutCaption,
  LogoNavRow,
  NavRow,
  SubLinkButton,
  UserMenuButton,
  UserMenuItemButton,
} from './styles';

// 로그아웃 로직 — 데스크톱 UserButton 과 모바일 드로어에서 공유한다.
function useLogout() {
  const safeLoginStorage = generateStorage();
  const { mutate: logoutMutation } = useMutation(auth.logout);
  return () => {
    try {
      logoutMutation();
      LoginStorage.removeToken();
      safeLoginStorage.remove(ADMIN);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      LoginStorage.removeToken();
      safeLoginStorage.remove(ADMIN);
      window.location.href = '/';
    }
  };
}

type NavGroup = { label: string; items: { label: string; link: string }[] };

// 모바일 드로어 그룹 구성 (회색 그룹 라벨 + 굵은 항목들). 데스크톱 내비와 항목 동일.
function getMobileGroups(isAdmin: boolean): NavGroup[] {
  const groups: NavGroup[] = [
    {
      label: '매물 관리',
      items: [
        { label: '새 매물 등록', link: '/room/create' },
        { label: '매물 리스트', link: '/room' },
      ],
    },
    {
      label: '계약 관리',
      items: [
        { label: '받은 가계약 신청', link: '/contract' },
        { label: '완료된 가계약 신청', link: '/contracted' },
      ],
    },
  ];

  if (isAdmin) {
    groups.push({
      label: '서비스 관리',
      items: [
        { label: 'Waitlist', link: '/service/waitlist' },
        { label: '멤버십', link: '/service/membership' },
        { label: 'Request a Room', link: '/service/request-a-room' },
      ],
    });
    groups.push({
      label: '관리자 메뉴',
      items: [
        { label: '대시보드', link: '/dashboard' },
        { label: '가입 승인', link: '/user/approval' },
        { label: '회원관리', link: '/user/manage' },
        { label: '쿠폰관리', link: '/coupon' },
        { label: '리워드 관리', link: '/reward' },
      ],
    });
  } else {
    groups.push({
      label: '계정 관리',
      items: [{ label: '프로필 수정', link: '/profile/edit' }],
    });
  }

  return groups;
}

function Dropdown({ children, title, link }: PropsWithChildren<{ title: string; link: string }>) {
  const navigate = useNavigate();
  return (
    <DropdownContainer>
      <LinkButton onClick={() => navigate(link)}>{title}</LinkButton>
      <DropdownBlock>{children}</DropdownBlock>
    </DropdownContainer>
  );
}

function LoginButton() {
  const navigate = useNavigate();
  return (
    <Wrap gap="8px" align="center">
      <Button onClick={() => navigate('/login')}>
        <Caption3 color="Red">로그인 / 회원가입</Caption3>
      </Button>
    </Wrap>
  );
}

function UserButton() {
  const [name] = useRecoilState(userSelectorFamily('name'));
  const handleLogout = useLogout();
  return (
    <Menu>
      <UserMenuButton>
        <Caption3 color="White">{name}님</Caption3>
      </UserMenuButton>
      <MenuItems anchor="bottom">
        <MenuItem>
          <UserMenuItemButton onClick={handleLogout}>
            <LogOutCaption color="Black">로그아웃</LogOutCaption>
          </UserMenuItemButton>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}

// 모바일 햄버거 메뉴 — Sheet 전체 화면 드로어. 상단 로그인/로그아웃, 그룹별 메뉴.
function MobileNav({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const [name] = useRecoilState(userSelectorFamily('name'));
  const handleLogout = useLogout();
  const isLogin = LoginStorage.isLogin();
  const groups = getMobileGroups(isAdmin);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="메뉴 열기"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <MenuIcon size={26} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        showClose={false}
        className="flex w-full max-w-none flex-col gap-0 overflow-y-auto border-0 bg-white p-0 sm:max-w-none"
      >
        <SheetHeader className="flex-row items-center justify-between px-6 pb-2 pt-5">
          <SheetTitle className="m-0 flex shrink-0 items-center">
            {/* 로고 이미지 좌측에 약 9.66% 투명 여백이 있어, 집 아이콘을 메뉴 텍스트와 같은 x에 맞추기 위해 당김 */}
            <img src="Logo.svg" alt="logo" width="150" height="34" className="-ml-[14px] max-w-none shrink-0" />
          </SheetTitle>
          <SheetClose asChild>
            <button type="button" aria-label="메뉴 닫기" className="flex items-center justify-center p-1">
              <CloseIcon size={26} />
            </button>
          </SheetClose>
          <SheetDescription className="sr-only">메뉴</SheetDescription>
        </SheetHeader>

        <nav className="flex flex-col px-6 pb-8">
          {/* 로그인 상태 / 계정 — 로그인/비로그인 모두 동일 행 높이(표준 68px) 유지 */}
          <div className="flex h-[68px] items-center justify-between border-b border-gray-20">
            {isLogin ? (
              <>
                <span className="text-[18px] font-medium text-black">{name}님</span>
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-full border border-gray-200 px-4 py-1.5 text-[14px] font-medium text-gray-40"
                  >
                    로그아웃
                  </button>
                </SheetClose>
              </>
            ) : (
              <SheetClose asChild>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-1 text-[18px] font-medium text-black"
                >
                  로그인 / 회원가입
                  <ChevronRight size={20} strokeWidth={2.2} />
                </button>
              </SheetClose>
            )}
          </div>

          {/* 이용 안내 */}
          <SheetClose asChild>
            <button
              type="button"
              onClick={() => navigate('/guide')}
              className="border-b border-gray-20 py-5 text-left text-[18px] font-medium text-black"
            >
              이용 안내
            </button>
          </SheetClose>

          {/* 그룹 메뉴 */}
          {groups.map((group) => (
            <div key={group.label} className="border-b border-gray-20 py-5">
              <p className="m-0 mb-4 text-[14px] font-medium text-[#8E8E8E]">{group.label}</p>
              <div className="flex flex-col gap-5">
                {group.items.map((item) => (
                  <SheetClose asChild key={item.label}>
                    <button
                      type="button"
                      onClick={() => navigate(item.link)}
                      className="text-left text-[18px] font-medium text-black"
                    >
                      {item.label}
                    </button>
                  </SheetClose>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

// 모바일에서 헤더(로고+햄버거)를 숨기는 인증/진입 페이지. 자체 중앙 로고/헤더를 가진다.
const MOBILE_HEADERLESS_PATHS = ['/login', '/admin/login', '/kakaologin', '/register'];

const AppHeader = () => {
  const location = useLocation();
  const [isAdmin] = useRecoilState(userSelectorFamily('isAdmin'));
  const navigate = useNavigate();
  // 헤더 구조는 "GNB가 데스크톱 간격(50px) 그대로 한 줄에 들어가는가"로 분기한다.
  // 본문이 모바일 레이아웃(<1280px)이어도 폭이 되면 GNB는 데스크톱 형태를 유지한다.
  // 실측(여백 24px 기준): 비관리자 GNB ~751px → 800px부터 수용.
  // 관리자는 서비스관리·관리자메뉴가 더 있어 ~860px 필요 → 900px 미만은 햄버거로 강등.
  const gnbFits = useMatchMedia(isAdmin ? '(min-width: 900px)' : '(min-width: 800px)');
  // 인증/진입 페이지의 헤더 미노출은 본문 레이아웃(모바일 <1280px)을 따른다 —
  // 해당 페이지들의 모바일 화면은 자체 상단 헤더(중앙 로고/회원가입 바)를 가진다.
  const isMobile = useIsMobile();
  const isGuidePage = location.pathname === '/guide';
  const handleClickLogo = () => {
    if (!LoginStorage.isLogin()) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      navigate('/dashboard');
    } else {
      navigate('/room');
    }
  };

  // 인증/진입 페이지는 모바일 레이아웃(<1280px)에서 상단 헤더 없이 자체 헤더만 노출
  if (isMobile && MOBILE_HEADERLESS_PATHS.includes(location.pathname)) {
    return null;
  }

  if (!gnbFits) {
    return (
      <>
        <Header color={isGuidePage ? 'Gray10' : 'White'} $mobile>
          <Wrap jutify="space-between" width="100%" align="center">
            <img
              src="Logo.svg"
              alt="logo"
              width="150"
              height="34"
              onClick={handleClickLogo}
              className="-ml-[14px] max-w-none shrink-0"
              style={{ cursor: 'pointer' }}
            />
            <MobileNav isAdmin={isAdmin} />
          </Wrap>
        </Header>
        <Spacing height={74} backgroundColor={isGuidePage ? 'rgba(250, 250, 250, 1)' : 'White'} />
      </>
    );
  }

  return (
    <>
      <Header color={isGuidePage ? 'Gray10' : 'White'}>
        <Wrap jutify="space-between" width="100%" height="37px" align="center">
          <LogoNavRow>
            <img
              src="Logo.svg"
              alt="logo"
              width="159px"
              height="36px"
              onClick={handleClickLogo}
              style={{ cursor: 'pointer' }}
            />
            <NavRow>
              <LinkButton onClick={() => navigate('/guide')}>이용안내</LinkButton>
              <Dropdown title="매물관리" link="/room">
                <SubLinkButton onClick={() => navigate('/room/create')}>새 매물 등록</SubLinkButton>
                <SubLinkButton onClick={() => navigate('/room')}>매물 리스트</SubLinkButton>
              </Dropdown>

              <Dropdown title="계약관리" link="/contract">
                <SubLinkButton onClick={() => navigate('/contract')}>받은 가계약 신청</SubLinkButton>
                <SubLinkButton onClick={() => navigate('/contracted')}>완료된 가계약 신청</SubLinkButton>
              </Dropdown>

              {isAdmin && (
                <Dropdown title="서비스관리" link="/service/waitlist">
                  <SubLinkButton
                    onClick={() => {
                      navigate('/service/waitlist');
                    }}
                  >
                    Waitlist
                  </SubLinkButton>
                  <SubLinkButton
                    onClick={() => {
                      navigate('/service/membership');
                    }}
                  >
                    멤버십
                  </SubLinkButton>
                  <SubLinkButton
                    onClick={() => {
                      navigate('/service/request-a-room');
                    }}
                  >
                    Request a Room
                  </SubLinkButton>
                  {/* <SubLinkButton
                    onClick={() => {
                      console.log('CWY');
                    }}
                  >
                    CWY
                  </SubLinkButton>
                  <SubLinkButton
                    onClick={() => {
                      console.log('계약동행');
                    }}
                  >
                    계약동행
                  </SubLinkButton> */}
                </Dropdown>
              )}

              {isAdmin ? (
                <Dropdown title="관리자메뉴" link="/dashboard">
                  <SubLinkButton onClick={() => navigate('/dashboard')}>대시보드</SubLinkButton>
                  <SubLinkButton onClick={() => navigate('/user/approval')}>가입 승인</SubLinkButton>
                  <SubLinkButton onClick={() => navigate('/user/manage')}>회원관리</SubLinkButton>
                  <SubLinkButton onClick={() => navigate('/coupon')}>쿠폰관리</SubLinkButton>
                  <SubLinkButton onClick={() => navigate('/reward')}>리워드 관리</SubLinkButton>
                </Dropdown>
              ) : (
                <Dropdown title="계정관리" link="/profile/edit">
                  <SubLinkButton onClick={() => navigate('/profile/edit')}>프로필 수정</SubLinkButton>
                </Dropdown>
              )}
            </NavRow>
          </LogoNavRow>
          {LoginStorage.isLogin() ? <UserButton /> : <LoginButton />}
        </Wrap>
      </Header>
      <Spacing height={99} backgroundColor={isGuidePage ? 'rgba(250, 250, 250, 1)' : 'White'} />
    </>
  );
};

export default AppHeader;
